import { createTheme, type Theme } from '@mui/material/styles';
// Teaches `createTheme` about the `MuiDataGrid` component key below.
import type {} from '@mui/x-data-grid-premium/themeAugmentation';
import { LightTheme } from '@/constants/theme';
import { EmbeddableGridPanel } from './EmbeddableGridPanel';

export const isEmbeddableWidgetBundle =
  import.meta.env.VITE_WIDGET === 'external';

/**
 * Marks the host mount node so the baseline below can be scoped to it.
 */
export const EMBEDDABLE_WIDGET_ROOT_CLASS = 'wa-embeddable-widget';

/**
 * Container-scoped replacement for `CssBaseline`.
 *
 * `CssBaseline` writes `html`, `body` and `*` rules into the host document, so
 * in an embedded page (Salesforce) the host's own chrome inherits the widget's
 * font, size and background from `body`. These rules apply the same reset to
 * the mount node instead, which every widget tree — including overlays, since
 * they portal into that same node — still inherits from.
 */
export function createEmbeddableBaselineStyles(theme: Theme) {
  const root = `.${EMBEDDABLE_WIDGET_ROOT_CLASS}`;

  return {
    [root]: {
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      boxSizing: 'border-box',
      margin: 0,
      color: theme.palette.text.primary,
      ...theme.typography.body1,
      backgroundColor: theme.palette.background.default,
    },
    [`${root} *, ${root} *::before, ${root} *::after`]: {
      boxSizing: 'border-box',
    },
    [`${root} strong, ${root} b`]: {
      fontWeight: theme.typography.fontWeightBold,
    },
  };
}

/**
 * Media query that never matches — forces MUI X pickers into dialog mode so
 * backdrops and portals stay scoped to the embeddable widget container.
 */
export const EMBEDDABLE_PICKER_DESKTOP_MODE_MEDIA_QUERY = '@media not all';

const embeddableOverlayDefaultProps = {
  disableScrollLock: true,
  disableAutoFocus: true,
  disableRestoreFocus: true,
} as const;

/**
 * Ensures the host mount node can contain position:absolute modals, drawers, and poppers.
 */
export function prepareEmbeddableWidgetContainer(element: HTMLElement): void {
  element.classList.add(EMBEDDABLE_WIDGET_ROOT_CLASS);
  element.style.position = 'relative';
  element.style.overflow = 'visible';
  element.style.isolation = 'isolate';
}

const scopedOverlayRootSx = {
  position: 'absolute' as const,
  zIndex: 1300,
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  '& .MuiBackdrop-root': {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
};

const scopedDialogRootSx = {
  ...scopedOverlayRootSx,
  '& .MuiDialog-container': {
    position: 'absolute',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

/**
 * Popover/Menu position using viewport coords (getBoundingClientRect) but default
 * paper is `position: absolute` inside the portaled modal root. In embeddable
 * widgets that root is offset from the viewport, so menus appear far from their
 * anchor — use fixed positioning so top/left align with the trigger button.
 */
const embeddablePopoverPaperSx = {
  position: 'fixed' as const,
};

const embeddablePopperDefaultProps = {
  disableScrollLock: true,
  popperOptions: {
    strategy: 'fixed' as const,
  },
};

/**
 * Theme overrides so Modal, Dialog, Drawer, Popover, and Popper mount inside the widget container
 * with backdrop/dimming limited to that box (not the full browser viewport).
 */
export function createEmbeddableWidgetTheme(
  portalContainer: HTMLElement,
): Theme {
  const getContainer = (): HTMLElement => portalContainer;

  return createTheme(LightTheme, {
    components: {
      MuiModal: {
        defaultProps: {
          container: getContainer,
          ...embeddableOverlayDefaultProps,
        },
        styleOverrides: {
          root: scopedOverlayRootSx,
        },
      },
      MuiDialog: {
        defaultProps: {
          container: getContainer,
          ...embeddableOverlayDefaultProps,
        },
        styleOverrides: {
          root: scopedDialogRootSx,
        },
      },
      MuiDrawer: {
        defaultProps: {
          container: getContainer,
          ...embeddableOverlayDefaultProps,
        },
        styleOverrides: {
          root: {
            ...scopedOverlayRootSx,
            '& .MuiDrawer-paper': {
              position: 'absolute',
            },
          },
        },
      },
      MuiPopover: {
        defaultProps: {
          container: getContainer,
          disableScrollLock: true,
        },
        styleOverrides: {
          paper: embeddablePopoverPaperSx,
        },
      },
      MuiPopper: {
        defaultProps: {
          container: getContainer,
          ...embeddablePopperDefaultProps,
        },
      },
      MuiMenu: {
        defaultProps: {
          container: getContainer,
          disableScrollLock: true,
        },
        styleOverrides: {
          paper: embeddablePopoverPaperSx,
        },
      },
      MuiDataGrid: {
        defaultProps: {
          // `resolveProps` deep-merges the `slots` prop, so grids keep their own
          // slot overrides (toolbar, columnsManagement, …) alongside this one.
          slots: {
            panel: EmbeddableGridPanel,
          },
        },
      },
    },
  });
}
